// #774 — opens the top-level OnCatch recorder only from a reporter gesture.
// The popup is a capture surface, not an uploader: it returns a Blob to the
// embedding widget, which retains the normal authenticated upload path.

import type { CaptureMode } from './capture-choice-modal.ts';
import type { SupportedLocale } from './i18n/index.ts';

export type HostedRecorderLaunchResult =
  | {
      ok: true;
      blob: Blob;
      filename: string;
      contentType: 'video/mp4' | 'video/webm' | 'audio/mp4' | 'audio/webm';
    }
  | { ok: false; reason: 'popup_blocked' | 'unavailable' | 'cancelled' | 'timed_out' | 'failed' };

export interface HostedRecorderLaunchOptions {
  mode: Exclude<CaptureMode, 'screen'>;
  workspaceId: string;
  widgetBaseUrl: string;
  apiBaseUrl: string;
  workspaceName: string;
  retentionDays: number;
  locale: SupportedLocale;
  getToken: () => Promise<string | null>;
  timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 20_000;
const MAX_PRE_COMPRESSION_BYTES = 50 * 1024 * 1024;

function randomChannel(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function validResult(
  value: unknown,
  mode: HostedRecorderLaunchOptions['mode'],
): value is {
  type: 'oncatch-hosted-result';
  channel: string;
  blob: Blob;
  filename: string;
  contentType: HostedRecorderLaunchResult extends { ok: true; contentType: infer T } ? T : never;
} {
  if (!value || typeof value !== 'object') return false;
  const message = value as Record<string, unknown>;
  if (message.type !== 'oncatch-hosted-result' || !(message.blob instanceof Blob)) return false;
  if (typeof message.filename !== 'string' || typeof message.contentType !== 'string') return false;
  if (message.blob.size <= 0 || message.blob.size > MAX_PRE_COMPRESSION_BYTES) return false;
  const contentType = message.contentType;
  return mode === 'audio'
    ? contentType === 'audio/webm' || contentType === 'audio/mp4'
    : contentType === 'video/webm' || contentType === 'video/mp4';
}

/**
 * Open first, then do asynchronous work. Moving `window.open()` after token
 * refresh would lose user activation and make legitimate reporters look like
 * they have a popup blocker.
 */
export function launchHostedRecorder(
  options: HostedRecorderLaunchOptions,
): Promise<HostedRecorderLaunchResult> {
  const popup = window.open('about:blank', '_blank', 'popup,width=520,height=720');
  if (!popup) return Promise.resolve({ ok: false, reason: 'popup_blocked' });

  const channel = randomChannel();
  const widgetOrigin = new URL(options.widgetBaseUrl).origin;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  return new Promise<HostedRecorderLaunchResult>((resolve) => {
    let settled = false;
    let initialized = false;
    const settle = (result: HostedRecorderLaunchResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      window.removeEventListener('message', onMessage);
      if (!popup.closed) popup.close();
      resolve(result);
    };
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== widgetOrigin || event.source !== popup || !event.data || settled) return;
      const message = event.data as Record<string, unknown>;
      if (message.channel !== channel) return;
      if (message.type === 'oncatch-hosted-ready' && !initialized) {
        initialized = true;
        popup.postMessage(
          {
            type: 'oncatch-hosted-init',
            channel,
            mode: options.mode,
            workspaceName: options.workspaceName,
            retentionDays: options.retentionDays,
            locale: options.locale,
          },
          widgetOrigin,
        );
        return;
      }
      if (message.type === 'oncatch-hosted-cancelled') {
        settle({ ok: false, reason: 'cancelled' });
        return;
      }
      if (message.type === 'oncatch-hosted-unavailable') {
        settle({ ok: false, reason: 'unavailable' });
        return;
      }
      if (validResult(message, options.mode)) {
        settle({
          ok: true,
          blob: message.blob,
          filename: message.filename,
          contentType: message.contentType,
        });
      }
    };
    const timeout = window.setTimeout(() => settle({ ok: false, reason: 'timed_out' }), timeoutMs);
    window.addEventListener('message', onMessage);

    void options
      .getToken()
      .then((token) => {
        if (!token || settled) {
          settle({ ok: false, reason: 'failed' });
          return;
        }
        const url = new URL('/record.html', widgetOrigin);
        url.searchParams.set('api', options.apiBaseUrl);
        // Fragments never go to the CDN/server or Referer header.
        url.hash = new URLSearchParams({
          channel,
          token,
          workspace: options.workspaceId,
          parent: location.origin,
        }).toString();
        popup.location.replace(url.toString());
      })
      .catch(() => settle({ ok: false, reason: 'failed' }));
  });
}
